import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext'; 
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { userService } from '../../../services/supabaseService';
import { APP_NAME } from '../../../constants';

const LoginForm = ({ onRoleSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');
  const returnStep = searchParams.get('step');
  const prefilledEmail = searchParams.get('email');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [isLogin, setIsLogin] = useState(true);

  useEffect(() => {
    if (prefilledEmail) {
      setFormData((prev) => ({ ...prev, email: prefilledEmail }));
    }
  }, [prefilledEmail]);

  // Redirection si l'utilisateur est déjà connecté
  useEffect(() => {
    if (user) {
      if (returnTo) {
        const stepQuery = returnStep !== null && returnStep !== '' ? `?step=${returnStep}` : '';
        navigate(`${returnTo}${stepQuery}`);
        return;
      }
      redirectToDashboard(user.user_metadata?.role || user.role);
    }
  }, [user, navigate, returnTo, returnStep]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({ ...prev, rememberMe: e?.target?.checked }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'L\'adresse email est requise';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleGoogleLogin = async () => {
    const googleClientId = import.meta.env?.VITE_GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = import.meta.env?.VITE_OAUTH_REDIRECT_URI;
    
    if (!googleClientId || googleClientId === 'your-google-oauth-client-id-here') {
      setErrors({ 
        oauth: 'Configuration Google OAuth manquante. Veuillez configurer VITE_GOOGLE_OAUTH_CLIENT_ID dans le fichier .env' 
      });
      return;
    }

    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${googleClientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=token&` +
      `scope=email profile&` +
      `state=google`;

    window.location.href = googleAuthUrl;
  };

  const handleFacebookLogin = async () => {
    const facebookAppId = import.meta.env?.VITE_FACEBOOK_APP_ID;
    const redirectUri = import.meta.env?.VITE_OAUTH_REDIRECT_URI;
    
    if (!facebookAppId || facebookAppId === 'your-facebook-app-id-here') {
      setErrors({ 
        oauth: 'Configuration Facebook OAuth manquante. Veuillez configurer VITE_FACEBOOK_APP_ID dans le fichier .env' 
      });
      return;
    }

    const facebookAuthUrl = `https://www.facebook.com/v18.0/dialog/oauth?` +
      `client_id=${facebookAppId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=email,public_profile&` +
      `state=facebook`;

    window.location.href = facebookAuthUrl;
  };

  const redirectToDashboard = (role) => {
    switch (role) {
      case 'client':
        navigate('/customer-account-portal');
        break;
      case 'technicien':
        navigate('/technician-daily-routes');
        break;
      case 'dispatch':
        navigate('/dispatch-control-center');
        break;
      case 'trainer':
        navigate('/service-catalog-manager');
        break;
      case 'admin':
        navigate('/analytics-dashboard');
        break;
      case 'logistician':
        navigate('/inventory-management');
        break;
      case 'manager':
        navigate('/inventory-management');
        break;
      default:
        navigate('/customer-account-portal');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      if (isLogin) {
        console.log('signin function: ', signIn);
        const { user: authUser, error } = await signIn(formData.email, formData.password);
        console.log('Authentication result:', authUser, error);
        
        if (error) {
          setErrors({ 
            submit: error.message || 'Email ou mot de passe incorrect' 
          });
        } else {
          const { data: userData } = await userService.getUserProfile(authUser.id);
          console.log('Logged in user data:', userData);
          
          if (formData.rememberMe) {
            localStorage.setItem('userRole', userData.role);
            localStorage.setItem('userEmail', userData.email);
          }
          sessionStorage.setItem('isAuthenticated', 'true');
          
          if (onRoleSelect && userData.role) {
            onRoleSelect(userData.role);
          }

          if (returnTo) {
            const stepQuery = returnStep !== null && returnStep !== '' ? `?step=${returnStep}` : '';
            navigate(`${returnTo}${stepQuery}`);
          } else {
            redirectToDashboard(userData.role);
          }
        }
      } else {
        setErrors({
          submit: 'L\'inscription n\'est pas encore implémentée. Utilisez la connexion.'
        });
      }
    } catch (err) {
      console.error('Erreur d\'authentification:', err);
      setErrors({ 
        submit: err.message || 'Une erreur est survenue. Veuillez réessayer.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestAccess = () => {
    navigate('/service-booking-wizard');
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setErrors({ email: 'Veuillez entrer votre email pour réinitialiser le mot de passe' });
      return;
    }
    
    try {
      alert(`Un email de réinitialisation a été envoyé à ${formData.email}`);
    } catch (err) {
      setErrors({ 
        submit: 'Erreur lors de l\'envoi de l\'email de réinitialisation' 
      });
    }
  };

  const handleMfaSubmit = async (e) => {
    e?.preventDefault();
    
    if (!mfaCode || mfaCode.length !== 6) {
      setErrors({ mfa: 'Veuillez entrer un code MFA valide à 6 chiffres' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (mfaCode === '123456') {
        const userRole = localStorage.getItem('userRole') || 'client';
        redirectToDashboard(userRole);
      } else {
        setErrors({ mfa: 'Code MFA invalide' });
      }
    } catch (err) {
      setErrors({ mfa: err.message || 'Erreur de vérification MFA' });
    } finally {
      setIsLoading(false);
    }
  };

  // Style commun pour les inputs
  const inputStyle = {
    background: "rgba(6, 24, 33, 0.72)",
    border: "1px solid rgba(125, 211, 252, 0.18)",
    color: "white",
    borderRadius: "0.75rem"
  };

  if (requiresMFA) {
    return (
      <div className="w-full max-w-md">
        <div className="glass-panel rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="brand-mark w-16 h-16 mx-auto mb-4">
              <Icon name="Shield" size={32} color="#FFFFFF" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              Authentification à deux facteurs
            </h2>
            <p className="text-sm text-gray-400">
              Entrez le code à 6 chiffres de votre application d'authentification
            </p>
          </div>

          <form onSubmit={handleMfaSubmit} className="space-y-6">
            <Input
              label="Code MFA"
              type="text"
              name="mfaCode"
              placeholder="123456"
              value={mfaCode}
              onChange={(e) => {
                setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                if (errors.mfa) setErrors(prev => ({ ...prev, mfa: '' }));
              }}
              error={errors?.mfa}
              required
              maxLength={6}
              className="text-center text-2xl tracking-widest"
              style={inputStyle}
            />

            {errors?.submit && (
              <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                <Icon name="AlertCircle" size={20} style={{ color: "#ef4444" }} />
                <p className="text-sm" style={{ color: "#ef4444" }}>{errors?.submit}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              iconName="Lock"
              iconPosition="left"
            >
              Vérifier le code
            </Button>

            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => {
                setRequiresMFA(false);
                setMfaCode('');
                setErrors({});
              }}
              style={{ color: "#9ca3af" }}
            >
              Retour à la connexion
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="glass-panel rounded-2xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-white mb-2">
            {isLogin ? `Connexion à ${APP_NAME}` : 'Créer un compte'}
          </h2>
          <p className="marketing-muted text-sm">
            {isLogin ? 'Accédez à votre espace professionnel' : 'Rejoignez notre plateforme professionnelle'}
          </p>
        </div>

        {returnTo === '/service-booking-wizard' || returnTo === '/quote-accept-booking' ? (
          <div className="mb-6 p-4 rounded-xl border border-cyan-300/20 bg-cyan-300/10">
            <p className="text-sm text-cyan-100">
              Connectez-vous pour reprendre votre réservation à l&apos;étape où vous l&apos;avez laissée.
            </p>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Adresse email"
            type="email"
            name="email"
            placeholder="votre@email.fr"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
            style={inputStyle}
          />

          <div className="relative">
            <Input
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="••••••••"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 marketing-muted hover:text-white transition-colors"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          {isLogin && (
            <div className="flex items-center justify-between">
              <Checkbox
                label="Se souvenir de moi"
                checked={formData?.rememberMe}
                onChange={handleCheckboxChange}
                labelClassName="text-gray-300"
              />
              <button
                type="button"
                onClick={handleForgotPassword}
                className="marketing-link text-sm transition-colors"
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}

          {errors?.submit && (
            <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
              <Icon name="AlertCircle" size={20} style={{ color: "#ef4444" }} />
              <p className="text-sm" style={{ color: "#ef4444" }}>{errors?.submit}</p>
            </div>
          )}

          {errors?.oauth && (
            <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: "rgba(234, 179, 8, 0.1)", border: "1px solid rgba(234, 179, 8, 0.2)" }}>
              <Icon name="AlertTriangle" size={20} style={{ color: "#eab308" }} />
              <p className="text-sm" style={{ color: "#eab308" }}>{errors?.oauth}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            iconName={isLogin ? "LogIn" : "UserPlus"}
            iconPosition="left"
          >
            {isLoading ? 'Chargement...' : (isLogin ? 'Se connecter' : 'S\'inscrire')}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setErrors({});
              }}
              className="marketing-link text-sm transition-colors"
            >
              {isLogin ? 'Pas de compte ? Créer un compte' : 'Déjà un compte ? Se connecter'}
            </button>
          </div>

          {isLogin && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-2 marketing-muted" style={{ background: "rgba(8, 31, 43, 0.96)" }}>Ou continuer avec</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 font-medium text-sm hover:-translate-y-0.5"
                  style={{ background: "white", color: "#374151", border: "1px solid #e5e7eb" }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>

                <button
                  type="button"
                  onClick={handleFacebookLogin}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm hover:-translate-y-0.5"
                  style={{ background: "#1877F2", color: "white" }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </button>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-2 marketing-muted" style={{ background: "rgba(8, 31, 43, 0.96)" }}>Ou</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={handleGuestAccess}
                iconName="UserPlus"
                iconPosition="left"
                className="border-cyan-300/30 bg-transparent text-cyan-200 hover:bg-cyan-300/10 hover:text-cyan-100"
              >
                Réserver sans compte
              </Button>
            </>
          )}
        </form>

        {/* Configuration OAuth */}
        <div className="mt-6 p-4 rounded-xl bg-cyan-300/5 border border-cyan-300/15">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={18} style={{ color: "#3b82f6" }} className="flex-shrink-0 mt-0.5" />
            <div className="text-xs" style={{ color: "#93c5fd" }}>
              <p className="font-semibold mb-1" style={{ color: "#60a5fa" }}>Configuration OAuth requise</p>
              <p>Pour activer la connexion Google et Facebook, configurez les clés suivantes dans votre fichier .env :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li style={{ color: "#9ca3af" }}>VITE_GOOGLE_OAUTH_CLIENT_ID</li>
                <li style={{ color: "#9ca3af" }}>VITE_FACEBOOK_APP_ID</li>
                <li style={{ color: "#9ca3af" }}>VITE_OAUTH_REDIRECT_URI</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Comptes de démonstration */}
        <div className="mt-6 p-4 rounded-xl bg-slate-950/20 border border-cyan-300/10">
          <h3 className="text-sm font-medium text-white mb-3">Comptes de démonstration :</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-medium" style={{ color: "#3b82f6" }}>Client :</span>
              <span className="text-gray-400">client@plumberpro.fr</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium" style={{ color: "#22c55e" }}>Technicien :</span>
              <span className="text-gray-400">technicien@plumberpro.fr</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium" style={{ color: "#eab308" }}>Dispatcher :</span>
              <span className="text-gray-400">dispatch@plumberpro.fr</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium" style={{ color: "#ef4444" }}>Admin :</span>
              <span className="text-gray-400">admin@plumberpro.fr</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;